import type { GateMetrics, Hint, Vec3 } from '../fsm/machine';
import { captureJpeg, deviceFingerprint, liveGates, openRearCamera, requestWakeLock, startFrameLoop, takeStill, type CameraHandle, type CapturedJpeg } from './camera';
import { evaluateGates, type GateThresholds } from './gates';
import { requestMotionPermission, StabilityRing, startImu, type ImuHandle } from './imu';
import { evaluateOrientation, orientationFromGravity, type Orientation, type OrientationLimits } from './orientation';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// LA SESSIONE DI CATTURA (WP1): fotocamera, sensore di movimento, schermo acceso, ciclo di controllo e scatto in UN posto solo.
// Prima la stessa sequenza viveva due volte, nel recorder e nella schermata operatore, e ogni correzione (fuoco, modello,
// gravità) andava fatta due volte. Il recorder e la schermata operatore sono due VISTE della stessa sessione: ciò che si
// registra in magazzino è ciò che l'operatore userà, per costruzione.
// Le dipendenze del browser sono iniettabili (`CaptureDeps`): la sequenza si prova con fotocamera e sensore finti.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface CaptureDeps {
  requestMotionPermission(): Promise<boolean>;
  startImu(): ImuHandle;
  openCamera(): Promise<CameraHandle>;
  requestWakeLock(): Promise<() => void>;
  startFrameLoop(video: HTMLVideoElement, onFrame: (nowMs: number) => void, maxFps?: number): () => void;
  liveGates(video: HTMLVideoElement, scratch: HTMLCanvasElement): GateMetrics;
  captureJpeg(video: HTMLVideoElement): Promise<CapturedJpeg>;
  takeStill(track: MediaStreamTrack): Promise<Blob | null>;
  deviceFingerprint(): Promise<{ fingerprint: string; model: string }>;
  utcNow(): string;
}

export const realDeps: CaptureDeps = {
  requestMotionPermission,
  startImu: () => startImu(),
  openCamera: openRearCamera,
  requestWakeLock,
  startFrameLoop,
  liveGates,
  captureJpeg: (video) => captureJpeg(video),
  takeStill,
  deviceFingerprint,
  utcNow: () => new Date().toISOString(),
};

/** Ciò che l'operatore deve sapere in questo istante: qualità dell'immagine, posizione della pistola, stabilità. */
export interface LiveState {
  /** Il richiamo di qualità (mosso, scuro, bruciato, riflesso), o null. */
  quality: Hint | null;
  orientation: Orientation | null;
  /** Il richiamo di posizione (troppo dall'alto, inclinata), o null. Guida, non blocca. */
  orientationHint: Hint | null;
  /** Anello di stabilità 0..1. */
  ring: number;
}

/** Uno scatto con tutto ciò che il sensore e la fotocamera sapevano in quell'istante. */
export interface CaptureResult {
  blob: Blob;
  widthPx: number;
  heightPx: number;
  gates: GateMetrics;
  /** La foto ferma a piena risoluzione scattata subito dopo il fotogramma, o null se il browser non la offre. */
  photo: Blob | null;
  /** La gravità: media delle letture prima e dopo la foto ferma (le due sono state trovate d'accordo entro `MAX_SHOT_DRIFT_DEG`). */
  gravity: Vec3 | null;
  angularSpeedDegPerS: number;
  orientation: Orientation | null;
  /** Impostazioni della traccia video, con l'esito del blocco del fuoco. */
  settings: Record<string, unknown>;
  focus: string;
  device: { fingerprint: string; model: string };
  capturedAtUtc: string;
}

export interface CaptureOptions {
  /** Soglie e limiti sono letti a ogni controllo: arrivano come dati (configurazione) anche dopo l'apertura della fotocamera. */
  thresholds: () => GateThresholds;
  limits: () => OrientationLimits;
  onLive?: (state: LiveState, nowMs: number) => void;
  signal?: AbortSignal;
}

const LOOP_FPS = 5;

/** Fra il fotogramma e la foto ferma passa quasi un secondo: se in quel tempo la pistola gira più di così, la gravità non è quella della foto. */
export const MAX_SHOT_DRIFT_DEG = 1.5;

/** L'angolo fra due letture della gravità, in gradi. */
export function gravityDriftDeg(a: Vec3, b: Vec3): number {
  const dot = a.x * b.x + a.y * b.y + a.z * b.z;
  const na = Math.hypot(a.x, a.y, a.z);
  const nb = Math.hypot(b.x, b.y, b.z);
  return (Math.acos(Math.max(-1, Math.min(1, dot / (na * nb)))) * 180) / Math.PI;
}

export class CaptureSession {
  private readonly ring = new StabilityRing();
  private stopLoop: () => void = () => undefined;
  private releaseLock: () => void = () => undefined;
  private closed = false;

  private constructor(
    private readonly video: HTMLVideoElement,
    private readonly scratch: HTMLCanvasElement,
    private readonly camera: CameraHandle,
    private readonly imu: ImuHandle,
    private readonly device: { fingerprint: string; model: string },
    private readonly options: CaptureOptions,
    private readonly deps: CaptureDeps,
  ) {}

  /** Esito del blocco del fuoco ('locked' o il motivo): un profilo di calibrazione vale per un fuoco fisso. */
  get focus(): string {
    return this.camera.focus;
  }

  get settings(): MediaTrackSettings {
    return this.camera.settings;
  }

  /** Apre sensore, fotocamera e schermo acceso e avvia il ciclo. Se qualcosa fallisce, o se `signal` scatta, chiude ciò che aveva aperto. */
  static async open(video: HTMLVideoElement, scratch: HTMLCanvasElement, options: CaptureOptions, deps: CaptureDeps = realDeps): Promise<CaptureSession> {
    const aborted = (): Error => new DOMException('Apertura della fotocamera annullata', 'AbortError');
    await deps.requestMotionPermission();
    const imu = deps.startImu();
    let camera: CameraHandle | null = null;
    let release: () => void = () => undefined;
    try {
      camera = await deps.openCamera();
      if (options.signal?.aborted) throw aborted();
      video.srcObject = camera.stream;
      await video.play();
      release = await deps.requestWakeLock();
      if (options.signal?.aborted) throw aborted();
      const device = await deps.deviceFingerprint();
      if (options.signal?.aborted) throw aborted();
      const session = new CaptureSession(video, scratch, camera, imu, device, options, deps);
      session.releaseLock = release;
      session.stopLoop = deps.startFrameLoop(video, (now) => session.tick(now), LOOP_FPS);
      return session;
    } catch (e) {
      release();
      camera?.stop();
      imu.stop();
      throw e;
    }
  }

  private tick(nowMs: number): void {
    if (this.closed || this.video.videoWidth === 0) return;
    const quality = evaluateGates(this.deps.liveGates(this.video, this.scratch), this.options.thresholds());
    const orientation = orientationFromGravity(this.imu.gravity());
    const orientationHint = evaluateOrientation(orientation, this.options.limits());
    const ring = this.ring.update(this.imu.angularSpeed(), nowMs);
    this.options.onLive?.({ quality, orientation, orientationHint, ring }, nowMs);
  }

  async shoot(): Promise<CaptureResult> {
    if (this.closed) throw new Error('Sessione di cattura chiusa');
    const shot = await this.deps.captureJpeg(this.video);
    const before = this.imu.gravity();
    const photo = await this.deps.takeStill(this.camera.track).catch(() => null);
    const after = this.imu.gravity();
    if (photo && before && after && gravityDriftDeg(before, after) > MAX_SHOT_DRIFT_DEG) {
      throw new Error('La fotocamera si è mossa durante lo scatto: tienila ferma e riprova.');
    }
    const gravity = before && after ? { x: (before.x + after.x) / 2, y: (before.y + after.y) / 2, z: (before.z + after.z) / 2 } : (after ?? before);
    return {
      blob: shot.blob,
      widthPx: shot.widthPx,
      heightPx: shot.heightPx,
      gates: shot.gates,
      photo,
      gravity,
      angularSpeedDegPerS: this.imu.angularSpeed(),
      orientation: orientationFromGravity(gravity),
      settings: { ...this.camera.settings, focusLock: this.camera.focus },
      focus: this.camera.focus,
      device: this.device,
      capturedAtUtc: this.deps.utcNow(),
    };
  }

  /** Ferma tutto; si può chiamare più volte. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.stopLoop();
    this.releaseLock();
    this.camera.stop();
    this.imu.stop();
  }
}
